import { createSlice } from '@reduxjs/toolkit';
import { callApi } from '../../utils/apiCall';
import { dispatch } from '../store';
import { updateError } from './user';

// ─── Initial State ────────────────────────────────────────────────────────────

const initialState = {
    mappings: [],
    loading: false,
    error: null,
};

// ─── Slice ────────────────────────────────────────────────────────────────────

const slice = createSlice({
    name: 'unbounceIntegration',
    initialState,
    reducers: {
        setLoading(state, action) {
            state.loading = action.payload;
            state.error = null;
        },
        setMappings(state, action) {
            state.mappings = action.payload;
            state.loading = false;
            state.error = null;
        },
        setError(state, action) {
            state.error = action.payload;
            state.loading = false;
        },
        reset(state) {
            Object.assign(state, initialState);
        },
    },
});

export default slice.reducer;

// ─── Thunks ───────────────────────────────────────────────────────────────────

export function getUnbounceMappings(practiceId) {
    return async () => {
        dispatch(slice.actions.setLoading(true));
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/unbounce-mappings/`);
            if (response.status === 200) {
                const { data } = response;
                let results;
                if (Array.isArray(data?.results)) {
                    ({ results } = data);
                } else if (Array.isArray(data)) {
                    results = data;
                } else {
                    results = [];
                }
                dispatch(slice.actions.setMappings(results));
            } else {
                const msg = response.data?.detail || 'Failed to load Unbounce mappings';
                dispatch(slice.actions.setError(msg));
            }
        } catch (err) {
            dispatch(slice.actions.setError(err.message || 'Something went wrong'));
        }
    };
}

export function createUnbounceMapping(practiceId, payload, onSuccess) {
    return async () => {
        dispatch(slice.actions.setLoading(true));
        try {
            const response = await callApi(
                `/api/v1/practices/${practiceId}/unbounce-mappings/`,
                'POST',
                payload
            );
            if (response.status === 201 || response.status === 200) {
                dispatch(updateError({ success: true, message: 'Unbounce page mapping created' }));
                dispatch(getUnbounceMappings(practiceId));
                if (onSuccess) onSuccess();
            } else {
                const msg =
                    response.data?.page_id?.[0] ||
                    response.data?.detail ||
                    response.data?.non_field_errors?.[0] ||
                    'Failed to create mapping';
                dispatch(slice.actions.setError(msg));
                dispatch(updateError({ success: false, message: msg }));
            }
        } catch (err) {
            const msg = err.message || 'Something went wrong';
            dispatch(slice.actions.setError(msg));
            dispatch(updateError({ success: false, message: msg }));
        }
    };
}

export function updateUnbounceMapping(practiceId, id, payload, onSuccess) {
    return async () => {
        dispatch(slice.actions.setLoading(true));
        try {
            const response = await callApi(
                `/api/v1/practices/${practiceId}/unbounce-mappings/${id}/`,
                'PATCH',
                payload
            );
            if (response.status === 200) {
                dispatch(updateError({ success: true, message: 'Unbounce mapping updated' }));
                dispatch(getUnbounceMappings(practiceId));
                if (onSuccess) onSuccess();
            } else {
                const msg =
                    response.data?.page_id?.[0] ||
                    response.data?.detail ||
                    response.data?.non_field_errors?.[0] ||
                    'Failed to update mapping';
                dispatch(slice.actions.setError(msg));
                dispatch(updateError({ success: false, message: msg }));
            }
        } catch (err) {
            const msg = err.message || 'Something went wrong';
            dispatch(slice.actions.setError(msg));
            dispatch(updateError({ success: false, message: msg }));
        }
    };
}

export function deleteUnbounceMapping(practiceId, id, onSuccess) {
    return async () => {
        dispatch(slice.actions.setLoading(true));
        try {
            const response = await callApi(
                `/api/v1/practices/${practiceId}/unbounce-mappings/${id}/`,
                'DELETE'
            );
            if (response.status === 204 || response.status === 200) {
                dispatch(updateError({ success: true, message: 'Unbounce mapping removed' }));
                dispatch(getUnbounceMappings(practiceId));
                if (onSuccess) onSuccess();
            } else {
                const msg = response.data?.detail || 'Failed to remove mapping';
                dispatch(slice.actions.setError(msg));
                dispatch(updateError({ success: false, message: msg }));
            }
        } catch (err) {
            const msg = err.message || 'Something went wrong';
            dispatch(slice.actions.setError(msg));
            dispatch(updateError({ success: false, message: msg }));
        }
    };
}
